import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Participant,
  RawReservation,
} from '../interfaces/reservation.interface';

/** Firestore limite l'opérateur `in` à 30 valeurs par requête. */
const IN_QUERY_LIMIT = 30;

const pick = (...vals: Array<string | undefined>) =>
  vals.find((v) => typeof v === 'string' && v.trim().length > 0) || '';

const makeDisplayName = (r: RawReservation): string => {
  const source = String(r.source || '').toLowerCase();

  // Champs possibles selon la source
  const first = r.firstname || r.firstName || r.prenom;
  const last = r.lastname || r.lastName || r.nom;

  if (source === 'giftcard') {
    // format giftcard : lastname + firstname
    const fn = pick(last, '').toUpperCase();
    const ln = pick(first, '');
    const fromFields = [ln, fn].filter(Boolean).join(' ').trim();
    if (fromFields) return fromFields;
  }

  // format standard : name
  const fromName = pick(r.name);
  if (fromName) return fromName.trim();

  // fallback : compose depuis les champs trouvables
  const composed = [pick(last), pick(first)].filter(Boolean).join(' ').trim();
  return composed || '—';
};

export const normalizeReservation = (r: RawReservation): Participant => {
  const email = pick(r.email, r.mail);
  const phone = pick(r.phone, r.tel, r.telephone);
  const displayName = makeDisplayName(r);
  const places = r.places ?? 1;
  const type = r.type ?? null;
  return { id: r.id, displayName, email, phone, places, type };
};

/** Tri alphabétique sur le nom affiché, insensible aux accents et à la casse. */
export const sortParticipants = (rows: Participant[]): Participant[] =>
  [...rows].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, 'fr', { sensitivity: 'base' })
  );

/**
 * Nombre de places occupées par une réservation.
 *
 * Une place = un plan de travail à l'atelier. Un duo, c'est deux personnes
 * autour d'un seul plan de travail : il compte donc pour **une** place, comme
 * un solo. C'est bien ce qu'écrit AddReservationPopUp (`places: 1`), et c'est
 * cohérent avec `remainingPlaces` côté atelier.
 */
export const reservationPlaces = (p: Participant): number =>
  Math.max(1, Number(p.places) || 0);

/**
 * Montant encaissé estimé pour une réservation. Le tarif duo couvre la place
 * entière (deux personnes), d'où l'absence de multiplication par les places.
 */
export const participantRevenue = (
  p: Participant,
  priceSolo: number,
  priceDuo: number
): number =>
  p.type === 'duo' ? priceDuo || 0 : (priceSolo || 0) * reservationPlaces(p);

/** Réservations d'un atelier, normalisées et triées. */
export const fetchParticipants = async (
  workshopId: string
): Promise<Participant[]> => {
  const snap = await getDocs(
    query(collection(db, 'reservations'), where('workshopId', '==', workshopId))
  );
  return sortParticipants(
    snap.docs.map((d) =>
      normalizeReservation({ id: d.id, ...(d.data() as any) })
    )
  );
};

/**
 * Réservations de plusieurs ateliers en une poignée de requêtes.
 * Retourne une entrée par id demandé, y compris les ateliers sans réservation.
 */
export const fetchParticipantsByWorkshop = async (
  workshopIds: string[]
): Promise<Record<string, Participant[]>> => {
  const result: Record<string, Participant[]> = {};
  workshopIds.forEach((id) => {
    result[id] = [];
  });

  const chunks: string[][] = [];
  for (let i = 0; i < workshopIds.length; i += IN_QUERY_LIMIT) {
    chunks.push(workshopIds.slice(i, i + IN_QUERY_LIMIT));
  }

  const snapshots = await Promise.all(
    chunks.map((chunk) =>
      getDocs(
        query(collection(db, 'reservations'), where('workshopId', 'in', chunk))
      )
    )
  );

  snapshots.forEach((snap) => {
    snap.docs.forEach((d) => {
      const data = d.data() as any;
      const bucket = result[data.workshopId];
      // Une résa peut pointer un atelier hors du lot demandé si l'id a changé
      if (bucket) bucket.push(normalizeReservation({ id: d.id, ...data }));
    });
  });

  Object.keys(result).forEach((id) => {
    result[id] = sortParticipants(result[id]);
  });

  return result;
};
